/**
 * feat-191-evaluator-role (C2 / ADR 0001 §A-9) — where a user lands after login.
 *
 * Before this feature every post-login redirect was the hardcoded literal
 * `/restaurant/pilot-dashboard`, in FOUR places: ScorecardLoginPage (the
 * already-authenticated redirect), LoginPage (email/password success),
 * PasswordChangePage (temp-password → set password → login), and App.tsx's bare
 * `/restaurant` route. That page is `StaffRoute`-gated (Admin | Manager |
 * Corporate), so an `evaluator` — who is deliberately none of those — landed on
 * an "Access denied" card on their very FIRST login, immediately after setting
 * their temp password, with no way to reach the one page they were hired to use.
 *
 * This helper is the single place that decision is made. All four sites call it.
 * Fixing three of four leaves a broken door, and the PasswordChangePage one is
 * the one that matters most (R5): it is the first screen a new contractor sees.
 *
 * NOTE — this is deliberately NOT in `src/utils/permissions.ts`. That file is an
 * allowlist that already denies an unknown role on all 12 capabilities, which is
 * exactly the behavior feat-191 wants for an evaluator (AC7). Adding `evaluator`
 * to it would turn this feature into a privilege-escalation bug. Choosing a
 * landing page is not a capability grant, so it lives here instead.
 */

/** The three `/restaurant/internal/interaction-evaluations*` routes are the entire evaluator surface (D3). */
export const EVALUATOR_LANDING_PATH = '/restaurant/internal/interaction-evaluations';

/** Unchanged for Admin / Manager / Corporate and for anyone else (feat-083). */
export const DEFAULT_LANDING_PATH = '/restaurant/pilot-dashboard';

/**
 * Post-login landing path for a management role.
 *
 * Case-insensitive, matching the backend (`UserContext._role_lower`, which
 * lowercases whatever string `management.role` holds) and the Title-case
 * normalization in AdminRoute / StaffRoute / EvaluatorRoute. `management.role`
 * is a free-form VARCHAR, so an unrecognized value is possible and must not
 * throw: everything that is not an evaluator keeps today's landing exactly.
 */
export function landingPathForRole(role?: string | null): string {
  const normalized = (role ?? '').trim().toLowerCase();
  return normalized === 'evaluator' ? EVALUATOR_LANDING_PATH : DEFAULT_LANDING_PATH;
}
